import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { buildApiUrl, buildAssetUrl, getPlaceholderImage as getFallbackImage } from '../config/constants';
import { getStoredAuthToken } from '../utils/auth';

interface BookCardProps {
  id: number;
  title: string;
  author: string;
  description: string;
  coverImage?: string;
  category?: string;
  fileName?: string;
  fileType?: string;
}

const BookCard: React.FC<BookCardProps> = ({ 
  id, 
  title, 
  author, 
  description, 
  coverImage, 
  category,
  fileName,
  fileType
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Debug info
  useEffect(() => {
    console.log('BookCard rendered for:', title);
    console.log('Cover image path:', coverImage);
  }, [title, coverImage]);
  
  // Gestionăm afișarea copertei corect
  const getCoverUrl = (coverPath: string | undefined | null) => {
    if (!coverPath) {
      return getPlaceholderImage();
    }

    return buildAssetUrl(coverPath);
  };
  
  const getPlaceholderImage = () => {
    return getFallbackImage(title, 300, 450);
  };
  
  const imgSrc = getCoverUrl(coverImage);
  console.log('Final image src:', imgSrc);
  
  const handleImageError = () => {
    console.error('Image failed to load:', coverImage);
    setImageError(true);
  };
  
  const downloadBook = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); 
    
    if (!fileName) {
      alert('Această carte nu are un fișier asociat pentru descărcare.');
      return;
    }
    
    try {
      setIsDownloading(true);
      const token = getStoredAuthToken();
      
      if (!token) {
        alert('Trebuie să fiți autentificat pentru a descărca cărți.');
        return;
      }

      const response = await fetch(buildApiUrl(`/books/${id}/download`), {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Nu s-a putut descărca fișierul.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Eroare la descărcarea cărții:', error);
      alert('Eroare la descărcarea cărții. Încercați din nou mai târziu.');
    } finally {
      setIsDownloading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition duration-300">
      <Link to={`/books/${id}`} className="block">
        <div className="aspect-[2/3] relative">
          <img 
            src={getCoverUrl(coverImage)} 
            alt={`Coperta cărții ${title}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null; // Previne loop infinit
              target.src = getPlaceholderImage();
            }}
          />
          
          {category && (
            <div className="absolute top-2 right-2">
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                {category}
              </span>
            </div>
          )}
          
          {fileName && fileType && (
            <div className="absolute bottom-2 right-2">
              <span className="px-2 py-1 bg-gray-800 bg-opacity-70 text-white text-xs font-medium rounded">
                {fileType.toUpperCase()}
              </span>
            </div>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">{title}</h3>
          <p className="text-gray-600 text-sm mb-2">de {author}</p>
          <p className="text-gray-700 text-sm line-clamp-3">
            {description?.substring(0, 150) || 'Fără descriere disponibilă.'}
            {description?.length > 150 ? '...' : ''}
          </p>
        </div>
      </Link>
    </div>
  );
};

export default BookCard; 
